import React, {useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { lockedLineTarget } from '../../Common/EditOrder/lockedLineTarget';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../Common';
import Hyperlink from '@ajat/design-system/Hyperlink';
import {
  HeadingLarge,
  HeadingSmall,
  StickyFooter,
} from '@ajat/design-system/Configurator';
import QuantityInput from '@ajat/design-system/QuantityInput';
import InfoTooltip from '@ajat/design-system/InfoTooltip';
import Preloader from '@ajat/design-system/Preloader';
import { usePageLoader } from '@ajat/design-system/PageLoader';
import Gallery from '../../Common/Product/Gallery';
import ClampedText from '../../Common/Product/ClampedText';
import AttributeSelector from '../../Common/Product/AttributeSelector';
import CLSAddons from './CLSAddons';
import MobileFocusModal from '@ajat/design-system/MobileFocus';
import SizeGuide from '../../Common/Product/SizeGuide';
import { DeliveryBlock } from '@ajat/design-system/SharedBlocks';
import useProductPage from '../../Common/Product/useProductPage';
import ClosedShopMessage from '../../Common/ClosedShop/ClosedShopMessage';
import SchoolClassGate from '../../Common/SchoolClassGate';
import { resolveShopDestination } from '../../../../services/shopDestination';
import useProduct from '../../../../hooks/useProduct';
import useVariation from '../../../../hooks/useVariation';
import useSchoolSelection from '../../../../hooks/useSchoolSelection';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { cartScopeToBind, storedClassForSchool } from '../../../../services/cartScope';
import { STORAGE_KEYS } from '../../../../hooks/useMenuItemNavigation';
import useForceSchoolSelection from '../../../../hooks/useForceSchoolSelection';
import useCompatibilityTree from '../../../../hooks/useCompatibilityTree';
import useControlVisibility, { findUnansweredRevealedSlugs } from '../../../../hooks/useControlVisibility';
import { useCart } from '../../../../context/CartContext';
import ThreeDGallery, { threeDSupported } from '../../Common/Product/ThreeDGallery';
import useVisualViewportVars from '../../Common/Product/useVisualViewportVars';
import { buildTextfieldFocusFields, EMPTY_FOCUS_FIELD } from '../../Common/Product/textfieldFocusFields';
import { buildThreeDProps, canonicalThreeD, threeDFromSnapshot } from '../../Common/Product/threeD/buildThreeDProps';
import {
  resolveConstraintGroups, propagateConstraint, reconcileConstraints, unavailableLinked as constraintUnavailable,
} from '../../Common/Product/constraints/attributeConstraints';
import useThreeDCamera from '../../Common/Product/threeD/useThreeDCamera';
import SchoolSwitchPopup from '../../Common/SchoolShop/SchoolSwitchPopup';
import { shopSwitchCopyFor } from '../../Common/SchoolShop/schoolSwitchCopy';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import { stampSchoolSelection, notifySchoolSelectionUpdated } from '../../../../services/schoolSelectionTtl';
import { smoothScrollToTop } from '../../../../utils/scroll';
import { ADDON_TOOLTIPS } from './addonData';
import { SIZE_GUIDE_TITLE, SIZE_GUIDE_ITEMS } from './sizeGuideData';
import productShortDescription from '../../../../utils/shortDescription';
import '../../Common/Product/ProductDetails.css';

/* ---------- Brand-specific addons (not stored in DB yet) ---------- */
const ADDONS = {
  navnetryk: { label: 'Navnetryk', price: '+49,-', placeholder: 'Skriv navnetryk', maxLength: 16, helperText: 'Max. 16 chars' },
  specialTekst: { label: 'Speciel tekst', price: '+49,-', placeholder: 'Skriv speciel tekst', maxLength: 16, helperText: 'Indskriv eks. snapchatnavn, instagram eller valgfri tekst. Max. 16 tegn' },
  flag: { label: 'Flag', placeholder: 'Vælg flag' },
  klassensBogstav: { label: 'Klassens bogstav', placeholder: 'Vælg bogstav' },
  lilleBrysttryk: { label: 'Lille Brysttryk', extraInfo: 'Skolens logo eller eget logo (Tryk)' },
  rygtryk: { label: 'Rygtryk', price: '+49,-', options: ['A4', 'A3'], favouriteIndex: 1 },
};

/** Class option label of the read-only mode's school + class picker. */
const dkClassLabel = (c) => `Klasse ${c}`;

const ProductDetails = () => {
  const { slug, schoolSlug: routeSchoolSlug, variationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editOrder = location.state?.editOrder ?? null;
  // Cart-line edit (from the shopping cart): pre-populate like editOrder,
  // but saving replaces the CART line (remove + re-add) instead of an order.
  const editCart = location.state?.editCart ?? null;

  // Variation route: /clothing/cls-dk/school-shop/:schoolSlug/product/:variationId
  // Standard PDP:    /clothing/cls-dk/product-details/:slug
  const isVariationRoute = Boolean(routeSchoolSlug && variationId);

  // Bare-PDP collection context from listing/collection-shop card links:
  // ?variation={collectionItemId} switches the payload to the collection
  // variation (adapted shape); ?collection={slug} marks the cart line as
  // collection-link origin (checkout then requires school+class).
  const bareSearch = new URLSearchParams(location.search);
  const bareVariationId = !isVariationRoute ? bareSearch.get('variation') : null;
  const collectionSlugParam = !isVariationRoute ? bareSearch.get('collection') : null;

  const { schoolSlug: savedSchoolSlug, className: savedClassName } = useSchoolSelection('clsdk');
  // READ-ONLY mode: a visitor without school + class may LOOK at the product (gallery / 3D,
  // name, default price, description) but gets no configurator and no add-to-cart — the
  // right column shows the school + class picker (SchoolClassGate) instead, and the forced
  // popup stays closed. Order / cart edits and collection links carry their own scope.
  const readOnly = !editOrder && !editCart && !collectionSlugParam && !(savedSchoolSlug && savedClassName);
  // No stored school+class → force the SchoolSelector open (brand-gated).
  // Skipped for order-edit, which carries its own scope.
  const { openShopBlocked } = useForceSchoolSelection({
    brandSlug: 'clsdk',
    // A school-shop URL already names the school (+ ?shop=): open the popup
    // with those chosen so the customer only picks the class.
    prefill: isVariationRoute ? { schoolSlug: routeSchoolSlug, shopSlug: new URLSearchParams(window.location.search).get('shop') } : null,
    hasSelection: Boolean(savedSchoolSlug && savedClassName),
    // Blocking only needs the school - a missing class is not school-less.
    hasSchool: Boolean(savedSchoolSlug),
    // Collection-link PDPs (?collection=) are rep-distributed links that
    // always work — no closed page, no forced selector popup; school + class
    // are collected at checkout instead.
    skip: Boolean(editOrder) || Boolean(editCart) || Boolean(collectionSlugParam) || readOnly,
    // The variation route carries its school scope in the URL — it is not
    // school-less shopping, so never show the closed page there.
    urlScoped: isVariationRoute,
    // DK: identification (school+shop+class) is MANDATORY on product pages —
    // the popup can't be dismissed, and after selecting the customer stays on
    // this page (variation routes: only when the picked school matches).
    mandatory: true,
    staySchool: isVariationRoute ? routeSchoolSlug : '*',
  });
  // When editing an order, price against the order's own school/class so the
  // overrides match what was bought (and the target price is reachable).
  const className = editOrder?.className ?? savedClassName;
  const schoolSlug = editOrder?.schoolSlug ?? savedSchoolSlug;

  const productHook = useProduct(
    isVariationRoute ? null : 'clsdk',
    isVariationRoute ? null : slug,
    schoolSlug,
    className,
    bareVariationId,
  );
  // Shop context for the variation: from the order being edited, else the
  // ?shop query carried from the school-shop card, so overrides resolve to the
  // exact shop the customer is in.
  const shopSlugForVariation = editOrder?.shopSlug ?? new URLSearchParams(location.search).get('shop') ?? null;
  const variationHook = useVariation(isVariationRoute ? 'clsdk' : null, routeSchoolSlug, variationId, className, shopSlugForVariation);

  const product = isVariationRoute ? variationHook.data    : productHook.data;
  const loading = isVariationRoute ? variationHook.loading : productHook.loading;
  usePageLoader(loading, { brand: 'CLS', label: 'Indlæser…' });
  const error   = isVariationRoute ? variationHook.error   : productHook.error;
  const { addItem, removeItem, setSchool: setCartSchool, school: cartSchool, clearCart, itemCount: cartItemCount } = useCart('clsdk');
  /* Deep-link entry: a shared configurator URL is often the customer's FIRST
   * page — only the SchoolShop page binds the cart's school, so such carts
   * checked out school-less (invisible in season/class reports, and the
   * shop's invoice-fee override could not resolve → method default fee).
   * When the cart has NO school yet, bind the variation URL's school; never
   * override an existing binding — the cross-school confirm flow stays on
   * the shop page. Checkout derives the shop (and its fee override) from
   * the school. The visitor's stored CLASS goes along when it belongs to this
   * school: the API prices by the cart's class, and a cart bound to the school
   * alone charged the default price although the page showed the class price. */
  const variationSchool = variationHook.school;
  useEffect(() => {
    if (!isVariationRoute || !variationSchool?.id) return;
    if (cartSchool?.id != null) return;
    const storedClass = storedClassForSchool(readFreshSelection(STORAGE_KEYS.clsdk), variationSchool.id);
    Promise.resolve(setCartSchool({
      school_id: variationSchool.id,
      ...(storedClass ? { school_class: storedClass } : {}),
    })).catch(() => {});
  }, [isVariationRoute, variationSchool?.id, cartSchool?.id, setCartSchool]);

  const [addingToCart, setAddingToCart] = useState(false);
  // 3D layout: a saved line snapshot seeds the preview on edit and wins until
  // the customer changes something; otherwise the live selections drive it.
  const [savedThreeD, setSavedThreeD] = useState(null);
  const savedThreeDArmRef = useRef(false);
  // One-shop-per-cart guard: the API refused this add with code
  // 'shop_mismatch' — hold the payload + both shops while the customer
  // decides whether to empty the cart and switch shop.
  const [shopSwitch, setShopSwitch] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);

  /* -- Local state -- */
  const [activeImage, setActiveImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [printYes, setPrintYes] = useState(true);
  const [rygtryk, setRygtryk] = useState('A4');
  const [navnetrykValue, setNavnetrykValue] = useState('');
  const [specialTekstValue, setSpecialTekstValue] = useState('');
  const [descExpanded, setDescExpanded] = useState(false);
  // The description is CSS-clamped (2 lines mobile, 3 desktop); only show the read-more link
  // when the text actually overflows the clamp (ClampedText measures it — the
  // clamp ellipsis alone is not clickable, which left desktop stuck).
  const [descClamped, setDescClamped] = useState(false); // set by ClampedText
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [attrInfo, setAttrInfo] = useState(null); // { html, title } for dynamic attribute popups
  const [sizeError, setSizeError] = useState('');
  // Flipped on the first blocked add-to-cart with an empty required
  // textfield; each field's inline error clears itself as it is filled.
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  /* -- Pre-populate from editOrder / editCart state -- */
  useEffect(() => {
    const src = editOrder ?? editCart;
    if (!src || !product) return;
    const opts = src.options ?? {};
    if (opts.color && product.colors) {
      const idx = product.colors.findIndex(
        (c) => c.name?.toLowerCase() === opts.color.toLowerCase()
      );
      if (idx >= 0) setSelectedColor(idx);
    }
    if (opts.size) setSelectedSize(opts.size);
    // Restore every dynamic attribute selection (incl. the priced ones) so the
    // line rebuilds to its original price.
    if (opts.attributes && typeof opts.attributes === 'object') {
      setSelectedAttributes(opts.attributes);
    }
    if (opts.addons?.navnetryk) setNavnetrykValue(opts.addons.navnetryk);
    if (opts.addons?.specialTekst) setSpecialTekstValue(opts.addons.specialTekst);
    if (opts.addons?.lilleBrysttryk != null) setPrintYes(opts.addons.lilleBrysttryk);
    if (opts.addons?.rygtryk) setRygtryk(opts.addons.rygtryk);
    if (src.quantity) setQuantity(src.quantity);
    if (src.options?.three_d) { setSavedThreeD(threeDFromSnapshot(src.options.three_d)); savedThreeDArmRef.current = true; }
  }, [editOrder, editCart, product]);

  // Any selection change after the seed hands the preview back to live state.
  useEffect(() => {
    if (!savedThreeD) return;
    if (savedThreeDArmRef.current) { savedThreeDArmRef.current = false; return; }
    setSavedThreeD(null);
  }, [selectedAttributes, selectedColor, selectedSize]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -- Lock in pinned values + canonical color index for variations -- */
  useEffect(() => {
    if (!product) return;
    // Default the canonical color to the first pinned color value, if any.
    const cAttr = (product.attributes ?? []).find((a) => a.slug === 'color');
    const firstPinnedId = cAttr?.pinnedValueIds?.[0];
    if (firstPinnedId != null) {
      const idx = cAttr.values.findIndex((v) => v.id === firstPinnedId);
      if (idx >= 0) setSelectedColor(idx);
    }
    // Auto-select pinned values for every other attribute (so they're priced + saved).
    setSelectedAttributes((prev) => {
      const next = { ...prev };
      (product.attributes ?? []).forEach((attr) => {
        if (attr.slug === 'color' || attr.slug === 'size') return;
        if (next[attr.slug] != null) return;
        const pinnedId = attr.pinnedValueIds?.[0];
        if (pinnedId != null) next[attr.slug] = pinnedId;
      });
      return next;
    });
  }, [product]);

  /* -- Derived product data -- */
  const images     = product?.images ?? [];
  const colors     = product?.colors ?? [];
  const sizes      = product?.sizes ?? [];
  const attributes = product?.attributes ?? [];

  /* -- Compatibility tree -- */
  const colorAttr = useMemo(() => attributes.find((a) => a.slug === 'color'), [attributes]);
  const sizeAttr  = useMemo(() => attributes.find((a) => a.slug === 'size'),  [attributes]);

  const unifiedSelections = useMemo(() => {
    const out = { ...selectedAttributes };
    const colorId = colorAttr?.values?.[selectedColor]?.id;
    const sizeId  = sizeAttr?.values?.find((v) => v.value === selectedSize)?.id;
    if (colorId != null) out.color = colorId;
    if (sizeId  != null) out.size  = sizeId;
    return out;
  }, [selectedAttributes, selectedColor, selectedSize, colorAttr, sizeAttr]);

  const {
    enabled: treeEnabled,
    allowedValueIdsByAttribute,
    soldOutValueIdsByAttribute,
    selectionSoldOut,
    path: attributePath,
    currentNodeImages,
  } = useCompatibilityTree({
    tree:           product?.compatibility_tree ?? [],
    selections:     unifiedSelections,
  });

  // Controls currently hidden (disabled): not rendered, not priced, not saved.
  const hiddenControlSlugs = useControlVisibility({
    hiddenSlugs: product?.hidden_attributes ?? [],
    rules:       product?.visibility_rules ?? [],
    selections:  unifiedSelections,
  });
  const visibleSelectedAttributes = useMemo(() => Object.fromEntries(
    Object.entries(selectedAttributes).filter(([slug]) => !hiddenControlSlugs.has(slug)),
  ), [selectedAttributes, hiddenControlSlugs]);

  /* -- Linked attributes (constraints of the variation's 3D settings) --
     Members of a group always hold the same choice (matched by label); live
     changes propagate in handleAttributeChange, this effect fills members that
     hold nothing yet (defaults, a restored line carrying the visible one only).
     Hidden members are synced too — hiding keeps them out of price/cart/3D. */
  const constraintGroups = useMemo(
    () => resolveConstraintGroups(product?.three_d, product?.attributes ?? []),
    [product],
  );
  const constraintGroupsRef = useRef(constraintGroups);
  constraintGroupsRef.current = constraintGroups;
  useEffect(() => {
    if (!constraintGroups.length) return;
    setSelectedAttributes((prev) => reconcileConstraints(prev, constraintGroups, hiddenControlSlugs));
  }, [constraintGroups, selectedAttributes, hiddenControlSlugs]);
  const unavailableLinked = useMemo(
    () => constraintUnavailable(selectedAttributes, constraintGroups, hiddenControlSlugs),
    [selectedAttributes, constraintGroups, hiddenControlSlugs],
  );

  // 3D preview props from the live selections (must sit after
  // hiddenControlSlugs — a `const` read before its declaration throws).
  const liveThreeD = useMemo(() => buildThreeDProps(product?.layout, product?.three_d, {
    attributes: product?.attributes ?? [], selectedAttributes, colors, selectedColor, hiddenControlSlugs, logoAssets: product?.logo_assets ?? null,
    seasonYear: product?.season_year ?? null,
  }), [product, selectedAttributes, colors, selectedColor, hiddenControlSlugs]);
  const threeD = savedThreeD ?? liveThreeD;
  // Camera presets requested by the controls (focus → view, textfield blur →
  // blur_view, deferred + cancelled by the next control's camera).
  const { threeDFocus, focusThreeD, blurThreeD } = useThreeDCamera(liveThreeD);
  // Render 3D only where WebGL works and hasn't failed; the snapshot above is
  // still saved either way (it is derived from the selections, not the canvas).
  const [threeDFailed, setThreeDFailed] = useState(false);
  const showThreeD = !!threeD && !threeDFailed && threeDSupported();

  // Revealed-by-rule detail dropdowns (Bogstav tryk, Flag tryk, ...) that are
  // still unanswered: the customer chose the paid trigger, so the detail is
  // mandatory — add-to-cart blocks on these like on required textfields.
  const unansweredRevealedSlugs = useMemo(() => findUnansweredRevealedSlugs({
    attributes,
    hiddenSlugs: product?.hidden_attributes ?? [],
    hiddenControlSlugs,
    selections: selectedAttributes,
  }), [attributes, product, hiddenControlSlugs, selectedAttributes]);

  const disabledColorIdxs = useMemo(() => {
    const allowed = allowedValueIdsByAttribute.color;
    if (!allowed || !colorAttr) return new Set();
    return new Set(
      colorAttr.values
        .map((v, i) => (allowed.has(v.id) ? null : i))
        .filter((i) => i !== null),
    );
  }, [allowedValueIdsByAttribute, colorAttr]);

  // Pinned color value ids → indices in `colors`, so the chips lock (a variation
  // that fixes the colour can't be changed).
  const pinnedColorIdxs = useMemo(() => {
    const ids = product?.pinnedColorValueIds ?? [];
    if (!ids.length || !colorAttr) return new Set();
    const set = new Set();
    colorAttr.values.forEach((val, i) => { if (ids.includes(val.id)) set.add(i); });
    return set;
  }, [product, colorAttr]);

  const disabledSizes = useMemo(() => {
    const allowed = allowedValueIdsByAttribute.size;
    if (!allowed || !sizeAttr) return new Set();
    return new Set(
      sizeAttr.values
        .filter((v) => !allowed.has(v.id))
        .map((v) => v.value),
    );
  }, [allowedValueIdsByAttribute, sizeAttr]);

  // Sold-out combos: hidden entirely (not just disabled).
  const soldOutColorIdxs = useMemo(() => {
    const soldOut = soldOutValueIdsByAttribute.color;
    if (!soldOut || !colorAttr) return new Set();
    return new Set(
      colorAttr.values
        .map((v, i) => (soldOut.has(v.id) ? i : null))
        .filter((i) => i !== null),
    );
  }, [soldOutValueIdsByAttribute, colorAttr]);

  const soldOutSizes = useMemo(() => {
    const soldOut = soldOutValueIdsByAttribute.size;
    if (!soldOut || !sizeAttr) return new Set();
    return new Set(
      sizeAttr.values.filter((v) => soldOut.has(v.id)).map((v) => v.value),
    );
  }, [soldOutValueIdsByAttribute, sizeAttr]);

  // When editing an already-paid order, options are NOT disabled. Instead the
  // footer shows the price to match and blocks "Update" until the total equals
  // the original paid amount (see priceMismatch below).
  const priceLocked = !!editOrder?.priceLocked;

  const disabledValueIdsByAttribute = useMemo(() => {
    const out = {};
    for (const attr of attributes) {
      if (attr.slug === 'color' || attr.slug === 'size') continue;
      const allowed = allowedValueIdsByAttribute[attr.slug];
      if (!allowed) continue;
      out[attr.slug] = new Set(
        attr.values.filter((v) => !allowed.has(v.id)).map((v) => v.id),
      );
    }
    return out;
  }, [allowedValueIdsByAttribute, attributes]);

  // Clear selections that just became invalid (e.g. color change shrank the
  // allowed size set). Keeps the cart payload consistent with what the UI
  // shows as enabled.
  useEffect(() => {
    if (!treeEnabled) return;
    setSelectedAttributes((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const slug of Object.keys(next)) {
        const allowed = allowedValueIdsByAttribute[slug];
        const soldOut = soldOutValueIdsByAttribute[slug];
        if ((allowed && !allowed.has(next[slug])) || (soldOut && soldOut.has(next[slug]))) {
          delete next[slug];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    if (selectedSize && (disabledSizes.has(selectedSize) || soldOutSizes.has(selectedSize))) {
      setSelectedSize(null);
    }
    if (selectedColor != null && soldOutColorIdxs.has(selectedColor)) {
      setSelectedColor(null);
    }
  }, [treeEnabled, allowedValueIdsByAttribute, soldOutValueIdsByAttribute, disabledSizes, soldOutSizes, soldOutColorIdxs, selectedSize, selectedColor]);

  // Gallery precedence: variation (CollectionItem) per-node images win, then
  // compatibility-tree node images, then plain product images.
  const variationNodeImagesMap = product?.nodeImages ?? null;
  const lastNodeId = attributePath[attributePath.length - 1];
  const variationNodeImages = (variationNodeImagesMap && lastNodeId != null)
    ? (variationNodeImagesMap[lastNodeId] ?? null)
    : null;
  const effectiveImages = (variationNodeImages && variationNodeImages.length > 0)
    ? variationNodeImages
    : ((treeEnabled && currentNodeImages.length > 0) ? currentNodeImages : images);
  // The image currently shown in the gallery - what the customer actually
  // picked (colour/variation overrides included) - is the cart thumbnail.
  const displayedImage = effectiveImages[activeImage] ?? effectiveImages[0];

  /* -- Shared hook -- */
  const {
    isMobile, focusField, isZoomed, setIsZoomed,
    galleryRef, scrollYBeforeZoom, activeFocusContainerRef,
    handleTouchStart, handleTouchEnd, openFocusMode, closeFocusMode,
  } = useProductPage({
    imageCount: effectiveImages.length,
    activeImage,
    setActiveImage,
    brand: 'CLS',
  });
  // Mobile focus mode on 3D pages: while the MobileFocusModal is open the 3D
  // column stays pinned above the card (Gallery.css .pp--typing) and sizes
  // itself to the visual viewport left over by the keyboard.
  // so both mobile editing paths size the column to the visible viewport.
  useVisualViewportVars(showThreeD && !!focusField);
  // Mobile focus mode ↔ 3D camera: on phones the inline textfield is read-only
  // and opens the MobileFocusModal instead (the trigger already plays the
  // field's camera); when the modal closes (keyboard dismissed / Done / tap
  // away) the field's blur camera plays — the inline input never blurs there.
  const prevFocusFieldRef = useRef(null);
  useEffect(() => {
    const prev = prevFocusFieldRef.current;
    prevFocusFieldRef.current = focusField;
    if (!focusField && prev) blurThreeD(prev);
  }, [focusField, blurThreeD]);

  /* -- Attribute change handler -- */
  const handleAttributeChange = useCallback((slug, valueId) => {
    setSelectedAttributes((prev) => propagateConstraint(prev, slug, valueId, constraintGroupsRef.current));
  }, []);

  /* -- Dynamic attribute "?" popup (renders the attribute's Hint HTML) -- */
  const handleAttrInfoOpen = useCallback((html, title) => {
    setAttrInfo({ html, title });
  }, []);

  /* -- Focus fields map for MobileFocusModal (BE-driven textfields, by slug) -- */
  const focusFields = buildTextfieldFocusFields({
    attributes, selectedAttributes, onChange: handleAttributeChange, hiddenControlSlugs,
  });
  const activeFocusData = (focusField && focusFields[focusField]) || EMPTY_FOCUS_FIELD;

  /* -- Price -- */
  const basePrice = product?.priceRaw ?? 0;
  // Attributes contribute additional_price two ways:
  //   - textfield: anchor's price applies when the customer typed something
  //   - select/color: the selected value's own additional_price
  // The canonical color/size selectors live on their own state but the
  // shared `selectedAttributes` map already mirrors color/size value ids
  // via the AttributeSelector path, so we cover them here too.
  const attributesAdditional = useMemo(() => {
    return attributes.reduce((sum, a) => {
      if (hiddenControlSlugs.has(a.slug)) return sum; // hidden control = disabled, not priced
      const sel = selectedAttributes[a.slug];
      if (a.type === 'textfield') {
        if (typeof sel !== 'string' || sel.trim() === '') return sum;
        return sum + (Number(a.values?.[0]?.additional_price) || 0);
      }
      const match = a.values?.find((v) => v.id === sel);
      return sum + (Number(match?.additional_price) || 0);
    }, 0);
  }, [attributes, selectedAttributes, hiddenControlSlugs]);
  const totalPrice = (basePrice + attributesAdditional) * quantity;

  // Paid orders: the edit must keep the same line price. Show the target and
  // block "Update" until the current total matches it.
  // Line total to keep: ordered unit price × ORDERED quantity (totalPrice is unit × quantity).
  const targetPrice = lockedLineTarget(editOrder);
  const priceMismatch = priceLocked && targetPrice != null
    && Math.abs(totalPrice - targetPrice) > 0.5;

  /* -- Cart-ready payload -- */
  const cartItem = useMemo(() => ({
    productId: product?.id ?? null,
    slug,
    name: product?.name ?? '',
    color: hiddenControlSlugs.has('color') ? null : (colors[selectedColor] ?? null),
    size: hiddenControlSlugs.has('size') ? null : selectedSize,
    attributes: visibleSelectedAttributes,
    // Static add-ons removed — everything in "Tilføjelser" is now a product
    // Attribute, carried via `attributes`.
    addons: null,
    quantity,
    unitPrice: basePrice,
    totalPrice,
  }), [product, slug, colors, selectedColor, selectedSize, visibleSelectedAttributes, hiddenControlSlugs, quantity, basePrice, totalPrice]);

  /* -- Update order handler (edit mode) -- */
  const handleUpdateOrder = useCallback(() => {
    if (sizes.length > 0 && !selectedSize && !hiddenControlSlugs.has('size')) {
      setSizeError('Vælg venligst en størrelse');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (priceMismatch) return;
    navigate(`/clothing/cls-dk/edit-order/${editOrder.orderNumber}`, {
      state: {
        updatedItem: {
          itemId: editOrder.itemId,
          section: editOrder.section,
          quantity,
          options: {
            color: hiddenControlSlugs.has('color') ? null : (colors[selectedColor]?.name || null),
            size: hiddenControlSlugs.has('size') ? null : selectedSize,
            attributes: visibleSelectedAttributes,
            addons: null,
            three_d: threeD ? canonicalThreeD(threeD) : null,
            // Refresh the cart thumbnail to match the chosen colour/variant.
            image: displayedImage?.src || displayedImage?.url || null,
          },
        },
      },
    });
  }, [selectedSize, priceMismatch, navigate, editOrder, colors, selectedColor, selectedAttributes, displayedImage, quantity]);

  /* -- Update cart line handler (cart-edit mode) --
   * The BE merges identical lines on add, so the old line is removed FIRST
   * (add-first would merge into it and the removal would wipe both); if the
   * re-add fails, the original line is restored. Server re-prices. */
  const handleUpdateCartItem = useCallback(async () => {
    if (sizes.length > 0 && !selectedSize && !hiddenControlSlugs.has('size')) {
      setSizeError('Vælg venligst en størrelse');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      const image = displayedImage?.src || displayedImage?.url || null;
      const payload = {
        itemType: 'product',
        productId: cartItem.productId,
        quantity: cartItem.quantity,
        unitPrice: cartItem.unitPrice,
        additionalPrice: attributesAdditional,
        options: {
          name: cartItem.name,
          slug: cartItem.slug || editCart?.options?.slug || null,
          image,
          color: cartItem.color?.name || null,
          size: cartItem.size,
          attributes: cartItem.attributes,
          addons: cartItem.addons,
          ...(threeD ? { three_d: canonicalThreeD(threeD) } : {}),
          ...(treeEnabled && attributePath.length > 0
            ? { attribute_path: attributePath }
            : {}),
          ...(product?.variationId
            ? {
                collectionItemId: product.variationId,
                ...(isVariationRoute ? { schoolSlug: routeSchoolSlug } : {}),
                ...(isVariationRoute && shopSlugForVariation ? { shopSlug: shopSlugForVariation } : {}),
                ...(collectionSlugParam ? { collectionSlug: collectionSlugParam } : {}),
              }
            : {}),
        },
      };
      await removeItem(editCart.itemId);
      try {
        await addItem(payload);
      } catch (err) {
        // Best-effort restore of the original line so it isn't lost.
        try {
          await addItem({
            itemType: 'product',
            productId: cartItem.productId,
            quantity: editCart.quantity || 1,
            options: editCart.options || {},
          });
        } catch { /* restore is best-effort */ }
        throw err;
      }
      navigate('/clothing/cls-dk/shopping-cart');
    } catch (err) {
      console.error('Cart item update failed', err);
    } finally {
      setAddingToCart(false);
    }
  }, [selectedSize, addingToCart, displayedImage, cartItem, addItem, removeItem, editCart, treeEnabled, attributePath, isVariationRoute, product, routeSchoolSlug, attributesAdditional, navigate, hiddenControlSlugs]);

  /* -- Add to cart handler -- */
  /* Mandatory identification (school + shop + class) before ANY add-to-cart.
   * Exempt: order/cart edits (they carry their own scope) and collection
   * links (school is collected at checkout). Returns the fresh stored
   * selection when complete, an empty object for exempt flows, or null after
   * opening the non-dismissible popup (caller must abort the add). */
  const requireIdentification = useCallback(() => {
    if (editOrder || editCart || collectionSlugParam) return {};
    const sel = readFreshSelection(STORAGE_KEYS.clsdk);
    if (sel?.school?.id && sel?.class) return sel;
    document.dispatchEvent(new CustomEvent('open-school-selector', {
      detail: {
        mandatory: true,
        staySchool: isVariationRoute ? routeSchoolSlug : '*',
        ...(isVariationRoute ? { prefill: { schoolSlug: routeSchoolSlug, shopSlug: new URLSearchParams(window.location.search).get('shop') } } : {}),
      },
    }));
    return null;
  }, [editOrder, editCart, collectionSlugParam, isVariationRoute, routeSchoolSlug]);

  const handleAddToCart = useCallback(async () => {
    const identification = requireIdentification();
    if (identification === null) return; // popup opened — no add without it
    if (sizes.length > 0 && !selectedSize && !hiddenControlSlugs.has('size')) {
      setSizeError('Vælg venligst en størrelse');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    // Required textfields (attr.is_required, e.g. Vogntrøjer's Navnetryk /
    // Nummertryk): block add-to-cart while any is empty — flips on the
    // per-field inline errors and scrolls to the first offender. Mirrors the
    // BE's 422 refusal in addItem.
    const missingRequired = (attributes || []).filter((a) =>
      a.type === 'textfield' && a.is_required && !hiddenControlSlugs.has(a.slug)
      && !(typeof selectedAttributes[a.slug] === 'string' && selectedAttributes[a.slug].trim()));
    if (missingRequired.length > 0 || unansweredRevealedSlugs.length > 0) {
      setShowRequiredErrors(true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('[data-required-empty]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (addingToCart) return;
    setAddingToCart(true);
    // Declared outside the try so the catch can hand the same payload to the
    // shop-switch popup for a retry.
    let addPayload = null;
    try {
      // Bind the FULL scope (school + shop + class) onto a school-less cart
      // BEFORE the first add, so checkout never has to guess the shop
      // (Shop::resolveForSchool is ambiguous for schools with two open shops) —
      // and the class onto an empty cart that only knows the school
      // (services/cartScope.js).
      const scope = cartScopeToBind({ cartSchool, itemCount: cartItemCount, identification, includeShop: !isVariationRoute });
      if (scope) {
        await Promise.resolve(setCartSchool(scope)).catch(() => {});
      }
      const image = displayedImage?.src || displayedImage?.url || null;
      addPayload = {
        itemType: 'product',
        productId: cartItem.productId,
        quantity: cartItem.quantity,
        unitPrice: cartItem.unitPrice,
        additionalPrice: attributesAdditional,
        options: {
          name: cartItem.name,
          slug: cartItem.slug,
          image,
          color: cartItem.color?.name || null,
          size: cartItem.size,
          attributes: cartItem.attributes,
          addons: cartItem.addons,
          ...(threeD ? { three_d: canonicalThreeD(threeD) } : {}),
          ...(treeEnabled && attributePath.length > 0
            ? { attribute_path: attributePath }
            : {}),
          ...(product?.variationId
            ? {
                collectionItemId: product.variationId,
                ...(isVariationRoute ? { schoolSlug: routeSchoolSlug } : {}),
                ...(isVariationRoute && shopSlugForVariation ? { shopSlug: shopSlugForVariation } : {}),
                ...(collectionSlugParam ? { collectionSlug: collectionSlugParam } : {}),
              }
            : {}),
        },
      };
      await addItem(addPayload);
      // Success: brief checkmark on the button + a 1s ease-in-out scroll to top.
      setAddSuccess(true);
      smoothScrollToTop(1000);
      setTimeout(() => setAddSuccess(false), 2000);
      document.dispatchEvent(new CustomEvent('open-minicart'));
    } catch (err) {
      if (err?.data?.code === 'shop_mismatch') {
        setShopSwitch({ payload: addPayload, cartShop: err.data.cartShop, lineShop: err.data.lineShop });
      } else {
        console.error('Add to cart failed', err);
        pushNotification({ type: 'error', title: 'Kunne ikke tilføje til kurven', message: err?.data?.error || err?.message || '' });
      }
    } finally {
      setAddingToCart(false);
    }
  }, [selectedSize, addingToCart, displayedImage, cartItem, addItem, treeEnabled, attributePath, attributesAdditional, isVariationRoute, product, routeSchoolSlug, requireIdentification, cartSchool?.id, cartSchool?.class, cartItemCount, setCartSchool, collectionSlugParam, hiddenControlSlugs, selectedAttributes, attributes, unansweredRevealedSlugs]);

  /* One-shop-per-cart: the customer confirmed emptying the cart to order
   * from the shop this product belongs to. Empty → bind that shop (school
   * and class unchanged) → re-issue the exact add that was refused. */
  const confirmShopSwitch = useCallback(async () => {
    if (!shopSwitch) return;
    const { payload, lineShop } = shopSwitch;
    setShopSwitch(null);
    setAddingToCart(true);
    try {
      await clearCart();
      if (cartSchool?.id && lineShop?.id) {
        await setCartSchool({ school_id: cartSchool.id, shop_id: lineShop.id, school_class: cartSchool.class ?? null });
      }
      try {
        const key = STORAGE_KEYS.clsdk;
        const sel = readFreshSelection(key);
        if (sel?.school?.id && lineShop?.id) {
          localStorage.setItem(key, JSON.stringify(stampSchoolSelection({
            ...sel, season: { id: lineShop.id, slug: lineShop.slug, name: lineShop.name },
          })));
          notifySchoolSelectionUpdated(key);
        }
      } catch { /* storage is best-effort */ }
      await addItem(payload);
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 2000);
      document.dispatchEvent(new CustomEvent('open-minicart'));
    } catch (err) {
      console.error('Shop switch failed', err);
      pushNotification({ type: 'error', title: 'Kunne ikke tilføje til kurven', message: err?.data?.error || err?.message || '' });
    } finally {
      setAddingToCart(false);
    }
  }, [shopSwitch, clearCart, setCartSchool, cartSchool?.id, cartSchool?.class, addItem]);

  /* Open shop disabled + no school on a selector-less brand → closed page. */
  // Read-only mode → the visitor picked school + class in the gate: go to THIS product in
  // their shop (its own variation, prices and options) → the shop page when the shop does
  // not sell it → the main page when the school has no open shop.
  const handleGateSelected = useCallback(async ({ school, season, class: cls }) => {
    const path = await resolveShopDestination({
      brandSlug: 'clsdk',
      feSlug: 'cls-dk',
      school,
      chosenShop: season,
      className: cls,
      target: {
        type: 'product',
        id: Number(isVariationRoute ? variationId : bareVariationId) || null,
        productId: product?.id ?? null,
        name: product?.name ?? '',
      },
      closedPath: '/clothing/cls-dk',
    });
    navigate(path, { replace: true });
  }, [navigate, isVariationRoute, variationId, bareVariationId, product?.id, product?.name]);

  if (openShopBlocked) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
        <ClosedShopMessage brandSlug="clsdk" feSlug="cls-dk" standalone />
      </div>
    );
  }

  /* -- Loading / error states -- */
  if (loading) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Preloader />
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <p>Product not found.</p>
        </main>
      </div>
    );
  }

  return (
    <div className={`pp ${isZoomed ? 'pp--zoomed' : ''} ${showThreeD ? 'pp--3d' : ''} ${showThreeD && focusField ? 'pp--typing' : ''}`}>
      {/* HEADER */}
      <div className="pp__header">
        <Header hideOnScroll={false} showTopBar={false} />
      </div>
      <div className="pp__header-mobile">
        <Header hideOnScroll={false} showTopBar={false} />
      </div>

      {/* MAIN */}
      <main className="pp__main">
        {showThreeD ? (
          <ThreeDGallery
            threeD={threeD}
            onError={() => setThreeDFailed(true)}
            focusView={threeDFocus.view}
            focusTick={threeDFocus.tick}
            zoomed={isZoomed}
            onZoomChange={setIsZoomed}
          />
        ) : (
        <Gallery
          alt={product?.name}
          images={effectiveImages}
          activeImage={activeImage}
          setActiveImage={setActiveImage}
          isZoomed={isZoomed}
          setIsZoomed={setIsZoomed}
          scrollYBeforeZoom={scrollYBeforeZoom}
          galleryRef={galleryRef}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={handleTouchEnd}
        />
        )}

        <div className="pp__configurator">
          {/* Product heading with description */}
          <HeadingLarge
            titleAs="h1"
            subtitle={productShortDescription(product)}
            title={product.name}
            price={product.price}
            description={
              <>
                <ClampedText text={product.description} expanded={descExpanded} onOverflowChange={setDescClamped} />
                {!descExpanded && descClamped && (
                  <Hyperlink
                    variant="small"
                    className="pdp__read-more"
                    onClick={(e) => { e.preventDefault(); setDescExpanded(true); }}
                    href="#"
                  >
                    Læs mere
                  </Hyperlink>
                )}
              </>
            }
          />

          {readOnly ? (
          <>
          {/* Read-only page: the colour row only, so the visitor can browse the
              colours (gallery / 3D follow) before identifying — no sizes, no
              add-ons, nothing priced; a colour choice stays local to this page. */}
          <AttributeSelector
            colors={colors}
            sizes={[]}
            attributes={[]}
            selectedColor={selectedColor}
            onColorChange={(i) => { setSelectedColor(i); focusThreeD('color'); }}
            labels={{ color: 'Farve' }}
            disabledColorIdxs={disabledColorIdxs}
            pinnedColorIdxs={pinnedColorIdxs}
            soldOutColorIdxs={soldOutColorIdxs}
            hiddenControlSlugs={hiddenControlSlugs}
          />
            <SchoolClassGate
              brandSlug="clsdk"
              storageKey={STORAGE_KEYS.clsdk}
              prefill={isVariationRoute ? { schoolSlug: routeSchoolSlug, shopSlug: shopSlugForVariation } : null}
              onSelected={handleGateSelected}
              title="Vælg din skole"
              text="Vælg din skole og klasse for at kunne tilpasse og bestille produktet."
              schoolLabel="Skole"
              shopLabel="Butik"
              classLabel="Klasse"
              classOptionLabel={dkClassLabel}
              buttonLabel="Fortsæt"
              busyLabel="Et øjeblik…"
            />
          </>
          ) : (
          <>
          <AttributeSelector
            colors={colors}
            sizes={sizes}
            attributes={attributes}
            selectedColor={selectedColor}
            selectedSize={selectedSize}
            selectedAttributes={selectedAttributes}
            onColorChange={(i) => { setSelectedColor(i); focusThreeD('color'); }}
            onSizeChange={(s) => { setSelectedSize(s); setSizeError(''); }}
            onAttributeChange={handleAttributeChange}
            onAttributeFocus={focusThreeD}
            openFocusMode={openFocusMode}
            isMobile={isMobile}
            onAttributeBlur={blurThreeD}
            sizeError={sizeError}
            labels={{ color: 'Farve', size: 'Størrelse', sizeGuide: 'Størrelses guide' }}
            onSizeGuideOpen={() => setIsSizeGuideOpen(true)}
            disabledColorIdxs={disabledColorIdxs}
            pinnedColorIdxs={pinnedColorIdxs}
            disabledSizes={disabledSizes}
            disabledValueIdsByAttribute={disabledValueIdsByAttribute}
            soldOutColorIdxs={soldOutColorIdxs}
            soldOutSizes={soldOutSizes}
            soldOutValueIdsByAttribute={soldOutValueIdsByAttribute}
            hiddenControlSlugs={hiddenControlSlugs}
            excludeNonColorExtras
          />

          {selectedSize && (
            <div className="pp__section">
              <HeadingSmall title="Antal" showSelection={false} />
              <QuantityInput value={quantity} onChange={setQuantity} min={1} max={99} />
            </div>
          )}

          <CLSAddons
            visible={selectedColor !== null && selectedSize !== null}
            addons={ADDONS}
            isMobile={isMobile}
            openFocusMode={openFocusMode}
            onTooltipOpen={setActiveTooltip}
            navnetrykValue={navnetrykValue}
            setNavnetrykValue={setNavnetrykValue}
            specialTekstValue={specialTekstValue}
            setSpecialTekstValue={setSpecialTekstValue}
            printYes={printYes}
            setPrintYes={setPrintYes}
            rygtryk={rygtryk}
            setRygtryk={setRygtryk}
            dynamicAttributes={attributes}
            selectedAttributes={selectedAttributes}
            onAttributeChange={handleAttributeChange}
            onAttributeFocus={focusThreeD}
            onAttributeBlur={blurThreeD}
            onInfoOpen={handleAttrInfoOpen}
            showRequiredErrors={showRequiredErrors}
            requiredEmptySlugs={new Set(unansweredRevealedSlugs)}
            disabledValueIdsByAttribute={disabledValueIdsByAttribute}
            hiddenControlSlugs={hiddenControlSlugs}
            unavailableLinked={unavailableLinked}
          />
          </>
          )}

          {/* "Køb risikofrit" block hidden on CLS DK (2026-10-05, user decision). */}
          {!editOrder && <DeliveryBlock time="Leveres fragtfrit" cost="" description="" />}
        </div>
      </main>

      {/* STICKY FOOTER — not in read-only mode: nothing can be added to the cart there */}
      {!readOnly && (
      <StickyFooter
        buttonSuccess={addSuccess}
        price={`${totalPrice},-`}
        priceError={priceMismatch}
        targetText={priceLocked && targetPrice != null ? `Skal matche ${targetPrice},-` : null}
        deliveryMessage={product?.deliveryInformation || null}
        deliveryTime="Leveres fragtfrit"
        deliveryPrice=""
        deliveryText=""
        showDelivery={!!product?.deliveryInformation || !editOrder}
        onButtonClick={editOrder ? handleUpdateOrder : editCart ? handleUpdateCartItem : handleAddToCart}
        buttonLabel={selectionSoldOut ? 'Udsolgt' : ((editOrder || editCart) ? 'Opdater' : 'Tilføj til kurv')}
        buttonDisabled={priceMismatch || selectionSoldOut}
        className={`pp__footer ${focusField ? 'pp__footer--hidden' : ''}`}
      />
      )}

      {/* SIZE GUIDE */}
      <SchoolSwitchPopup
        isOpen={!!shopSwitch}
        brandSlug="clsdk"
        copy={shopSwitchCopyFor('clsdk', { from: shopSwitch?.cartShop?.name, to: shopSwitch?.lineShop?.name })}
        onBack={() => setShopSwitch(null)}
        onConfirm={confirmShopSwitch}
      />
      <SizeGuide isOpen={isSizeGuideOpen} onClose={() => setIsSizeGuideOpen(false)} title={SIZE_GUIDE_TITLE} items={SIZE_GUIDE_ITEMS} overrideHtml={product?.sizeguide_override || null} />

      {/* ADDON TOOLTIP */}
      <InfoTooltip
        isOpen={!!activeTooltip}
        onClose={() => setActiveTooltip(null)}
        ariaLabel={activeTooltip ? ADDON_TOOLTIPS[activeTooltip].title : ''}
      >
        {activeTooltip && (
          <>
            <h3 className="pp-addon-tip__title typo-h3">
              {ADDON_TOOLTIPS[activeTooltip].title}
            </h3>
            {ADDON_TOOLTIPS[activeTooltip].image && (
              <div className="pp-addon-tip__image-wrap">
                <img
                  src={ADDON_TOOLTIPS[activeTooltip].image}
                  alt={ADDON_TOOLTIPS[activeTooltip].title}
                  className="pp-addon-tip__image"
                />
              </div>
            )}
            {ADDON_TOOLTIPS[activeTooltip].description && (
              <div className="pp-addon-tip__description typo-body">
                {ADDON_TOOLTIPS[activeTooltip].description.split('\n\n').map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
          </>
        )}
      </InfoTooltip>

      {/* DYNAMIC ATTRIBUTE HINT-HTML POPUP */}
      <InfoTooltip
        isOpen={!!attrInfo}
        onClose={() => setAttrInfo(null)}
        ariaLabel={attrInfo?.title || ''}
      >
        {attrInfo && (
          <div
            className="pp-attr-hint typo-body"
            // Content is brand-authored admin copy (raw HTML with embedded CSS).
            dangerouslySetInnerHTML={{ __html: attrInfo.html }}
          />
        )}
      </InfoTooltip>

      {/* MOBILE FOCUS MODAL */}
      {isMobile && (
        <MobileFocusModal
          focusField={focusField}
          activeFocusData={activeFocusData}
          closeFocusMode={closeFocusMode}
          containerRef={activeFocusContainerRef}
        />
      )}
    </div>
  );
};

export default ProductDetails;
